import React from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router';
import * as ls from 'local-storage';
import moment from 'moment';

import {
  STORAGE_FORCE_LOG_OUT,
  SECURITY_TIMER_SECONDS,
  STORAGE_IDLE_TIME_UTC,
  MILISECONDS_TO_SECONDS,
} from 'Common/Constants';
import { getSidebarMessages } from 'Common/UI/state/selectors';
import {
  connect as signalRConnect,
  disconnect as signalRDisconnect,
} from 'API/SignalR/state/actions';
import {
  SIGNALR_STATUS_DISCONNECTED,
  SIGNALR_STATUS_DISCONNECTING,
  SIGNALR_STATUS_ERRORED,
} from 'API/SignalR/state/types';
import * as CommonStateSelectors from 'State/selectors';
import { token } from 'API/MPWApi';

import {
  logOutUser,
  checkAccessInfo,
  isSessionExpired,
} from './SecurityHelpers';
import * as SecurityActions from './state/actions';
import { clearState } from '../../state/actions';
import { getIsReadOnly } from '../Selectors/ReadOnlySelector';

const disconnectedSignalRStatuses = [
  SIGNALR_STATUS_DISCONNECTED,
  SIGNALR_STATUS_DISCONNECTING,
  SIGNALR_STATUS_ERRORED,
];

class WithSecurity extends React.Component {
  timer = null;
  OfflineTimerStartTime = null;
  OfflineTime = null;

  // Checks if the user can view the page based on the current state of the app and their credentials
  isValid = () => {
    const { onlineAndServerUp, hash } = this.props;

    const sessionExpired = isSessionExpired(
      ls.get(STORAGE_IDLE_TIME_UTC),
      moment.utc()
    );

    // return valid if we are online, the server is up, and we have a valid token, and session.
    if (onlineAndServerUp && token.isValid() && !sessionExpired) {
      return true;
    }

    // return valid if we are offline, or the server is down, have a password hash, and valid session.
    if (!onlineAndServerUp && hash && !sessionExpired) {
      return true;
    }

    return false;
  };

  componentWillMount() {
    const { history } = this.props;

    if (!this.isValid()) {
      this.logout(false);
      return;
    }

    if (
      !checkAccessInfo(
        this.props.accessInfo,
        this.props.history.location.pathname
      )
    ) {
      history.replace('/notfound');
    }
  }

  logout = (rememberLastUrl = true) => {
    const { logOutAction, history } = this.props;

    logOutAction();
    logOutUser(history, rememberLastUrl);
  };

  logoutAllTabs = (value, old) => {
    if (!old && value) {
      console.warn('We are also logging out other open tabs.');
      logOutUser(this.props.history, false);
    }
  };

  startLogOfflineTimer = () => {
    this.OfflineTimerStartTime = moment();
  };

  stopLogOfflineTimer = () => {
    this.OfflineTime = moment() - this.OfflineTimerStartTime;
  };

  stopTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.timer = null;
  };

  startTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
    }

    this.timer = setInterval(this.onTimer, SECURITY_TIMER_SECONDS * 1000);

    this.onTimer();
  };

  onTimer = () => {
    if (!this.isValid()) {
      this.logout();
      return;
    }

    //check to see if we need to refresh our token
    if (token.isTimeToRenew()) {
      token.refresh();
    }
  };

  getOfflineLogMessage = () => {
    const { userName } = this.props;

    let message = String.Empty;

    message = `USER: ${userName} was offline for ${
      this.OfflineTime / MILISECONDS_TO_SECONDS
    } second(s)`;

    return message;
  };

  componentDidMount() {
    const { onlineAndServerUp, signalRStatus, signalRConnect } = this.props;

    ls.on(STORAGE_FORCE_LOG_OUT, this.logoutAllTabs);

    if (onlineAndServerUp) {
      if (disconnectedSignalRStatuses.includes(signalRStatus)) {
        signalRConnect();
      }

      this.startTimer();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      onlineAndServerUp,
      signalRStatus,
      signalRConnect,
      clearProgressMessages,
      dispalyNetworkInteruptionUIMessage,
      sideBarMessages,
    } = this.props;

    // went offline, stop the timer to prevent logout
    if (!onlineAndServerUp && this.timer) {
      this.stopTimer();
      this.startLogOfflineTimer();

      if (Array.isArray(sideBarMessages) && sideBarMessages.length > 0) {
        clearProgressMessages();
        dispalyNetworkInteruptionUIMessage();
      }
    }

    // went back online
    if (onlineAndServerUp && !this.timer) {
      const { logOfflineTime } = this.props;

      this.startTimer();
      this.stopLogOfflineTimer();

      logOfflineTime(this.getOfflineLogMessage());
    }

    if (
      !prevProps.onlineAndServerUp &&
      onlineAndServerUp &&
      [SIGNALR_STATUS_ERRORED, SIGNALR_STATUS_DISCONNECTED].includes(
        signalRStatus
      )
    ) {
      signalRConnect();
    }
  }

  componentWillUnmount() {
    this.stopTimer();
    ls.off(STORAGE_FORCE_LOG_OUT, this.logoutAllTabs);
  }

  render() {
    const { component: WrappedComponent, ...rest } = this.props;
    return this.isValid() ? (
      <WrappedComponent {...rest} />
    ) : (
      <Redirect to="/" />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    accessInfo: CommonStateSelectors.getAccessInfo(state),
    hash: CommonStateSelectors.getHash(state),
    isReadOnly: getIsReadOnly(state),
    signalRStatus: state.SignalRState.status,
    onlineAndServerUp:
      CommonStateSelectors.getOnline(state) &&
      CommonStateSelectors.getServerUp(state),
    isOnline: CommonStateSelectors.getOnline(state),
    userName: CommonStateSelectors.getUserName(state),
    sideBarMessages: getSidebarMessages(state),
  };
};

const mapDispatchToProps = {
  clearState,
  signalRConnect,
  signalRDisconnect,

  ...SecurityActions,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(WithSecurity)
);



// WEBPACK FOOTER //
// ./src/common/Security/WithSecurity.js